/* eslint-disable */
import addDays from 'date-fns/add_days';
import format from 'date-fns/format';
import get from 'lodash/get';
import moment from 'moment';
import { EmptyError, of, Observable } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, map, retry, tap } from 'rxjs/operators';
import {
  ORDER_API_DATE_FORMAT,
  ORDER_CANCEL_CUTOFF_DAYS_FROM_TODAY,
  ORDER_CANCEL_CUTOFF_TIME,
  ORDER_CONFIRM_CUTOFF_DAYS_FROM_TODAY,
  ORDER_CONFIRM_CUTOFF_TIME,
  ORDER_MAX_DAYS_FROM_TODAY,
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_TO_BE_CONFIRMED,
} from '../../Constants/Order/Order.constants';
import { ENTRY_PORTAL_CUSTOMER_NAME } from '../../Entries/PortalCustomer/PortalCustomer.entry.constants';
import getFilters from '../../State/Store/Filters/Selectors/Filters.selector';
import getFiltersSelectedAccount from '../../State/Store/Filters/Selectors/FiltersSelectedAccount.selector';
import isBeforeCutOff from '../../Support/DateTime/IsBeforeCutOff/DateTimeIsBeforeCutOff.support';
import envGet from '../../Support/Env/Get/EnvGet.support';
import createContainerIframe from '../../Support/Html/CreateContainerIframe';
import isIOS from '../../Support/Is/IOS/IsIOS.support';
import helpers from '../../Support/Text';
import { buildUrl } from '../../Support/Url';
import './Order.service.i18n';
import updateOrderService, { UpdateOrderServicePayload } from './services/update-order';
import submitPlusLoadService from './services/submit-plus-load';
import { Order } from 'types/Order';
import getActivityLogService from './services/get-activity-log';
import isTooLateForPlusLoadService from './services/is-too-late-for-plus-load';
import { Account } from 'types/Account';
import getDefaultProjectForAccountService from './services/get-default-project-for-account';

const FEATURE_TOGGLE_OKTA_SUPPORT = envGet({ key: `REACT_APP_BORAL_FEATURE_TOGGLE_OKTA_SUPPORT` }) === `true`;

const baseUrl = envGet({ key: `REACT_APP_BORAL_BASE_URL` }) as string;
const baseUrlFlitch = envGet({ key: `REACT_APP_BORAL_BASE_URL_FLITCH` }) as string;
const ordersUrl = envGet({ key: `REACT_APP_BORAL_ORDERS_PORTAL_URL` }) as string;
const orderDetailsUrl = envGet({ key: `REACT_APP_BORAL_ORDER_DETAILS_PORTAL_URL` }) as string;
const docketsListUrl = envGet({ key: `REACT_APP_BORAL_DOCKETS_LIST_URL` }) as string;
const downloadDocketsCsvUrl = envGet({ key: `REACT_APP_BORAL_DOWNLOAD_DOCKETS_CSV_URL` }) as string;
const downloadDocketsPdfUrl = envGet({ key: `REACT_APP_BORAL_DOWNLOAD_DOCKETS_PDF_URL` }) as string;
const BORAL_DELIVERY_DOCKET_URL = envGet({ key: `REACT_APP_BORAL_DELIVERY_DOCKET_URL` }) as string;
const BORAL_TRUCKS_MAP_URL = envGet({ key: `REACT_APP_BORAL_TRUCKS_MAP_URL` }) as string;
const ordersAddressesUrl = envGet({ key: `REACT_APP_BORAL_ORDERS_ADDRESSES_PORTAL_URL` }) as string;
const ordersDetailsDeliveriesUrl = envGet({ key: `REACT_APP_BORAL_ORDER_DETAILS_DELIVERIES_PORTAL_URL` }) as string;
const ordersConfirmationUrl = envGet({ key: `REACT_APP_BORAL_ORDERS_CONFIRMATION_PORTAL_URL` }) as string;
const ordersLinkedUrl = envGet({ key: `REACT_APP_BORAL_ORDERS_LINKED_PORTAL_URL` }) as string;
const ordersReportsUrl = envGet({ key: `REACT_APP_BORAL_ORDERS_REPORTS` }) as string;
const ordersCompletedDeliveryUrl = envGet({ key: `REACT_APP_BORAL_COMPLETED_DELIVERY_URL` }) as string;
const projectsUrl = envGet({ key: `REACT_APP_BORAL_PROJECTS_URL` }) as string;
const accountsUrl = envGet({ key: `REACT_APP_BORAL_ACCOUNTS_LIST_URL` }) as string;
const holidaysUrl = envGet({ key: `REACT_APP_BORAL_HOLIDAYS_LIST_URL` }) as string;
const accountConfigUrl = envGet({ key: `REACT_APP_BORAL_ACCOUNT_CONFIG_URL` }) as string;
const projectConfigUrl = envGet({ key: `REACT_APP_BORAL_PROJECT_CONFIG_URL` }) as string;
const productsUrl = envGet({ key: `REACT_APP_BORAL_PRODUCTS_URL` }) as string;
const placeOrderUrl = envGet({ key: `REACT_APP_BORAL_PLACE_ORDER_URL` }) as string;
const updateOrderUrl = envGet({ key: `REACT_APP_BORAL_UPDATE_ORDER_URL` }) as string;
const BORAL_ACCOUNT_DEFAULT_PROJECT = envGet({ key: `REACT_APP_BORAL_ACCOUNT_DEFAULT_PROJECT` }) as string;

/**
 * OrderService
 * @class OrderService
 */
class OrderService {
  /**
   * The base url to request orders from
   * @type {string}
   * @memberof OrderService
   */
  _baseUrl = `/`;

  /**
   * The `AuthService`
   * @type {AuthService}
   * @memberof OrderService
   */
  authService: any;

  /**
   * The `HttpService`
   * @type {HttpService}
   * @memberof OrderService
   */
  httpService: any;

  /**
   * Creates an instance of `OrderService`
   * @param {OrderServiceCreateDto} dto The params required to create an instance of `OrderService`
   * @memberof OrderService
   */
  constructor({ authService, httpService }: any) {
    this.authService = authService;
    this.httpService = httpService;
  }

  /**
   * Fetch all known orders
   * @param {any} state The state
   * @returns {Promise<Array<OrderEntity>>} The orders
   * @memberof OrderService
   */
  all = async (state: any) => {
    const filters = getFilters(state);
    const headers = this.authService.createHeaders();
    const { account: billToAccount, numberOfDays, startDate, orderStatus, project } = filters;
    const status = orderStatus.length > 0 ? orderStatus.join(`,`) : ``;
    const params = {
      billToAccount,
      fromDate: format(startDate, ORDER_API_DATE_FORMAT),
      status,
      toDate: format(addDays(startDate, numberOfDays - 1), ORDER_API_DATE_FORMAT),
      project,
    };
    // do not retrieve orders after 90days from now
    if (
      moment(startDate)
        .add(ORDER_MAX_DAYS_FROM_TODAY, `days`)
        .isAfter(moment().add(ORDER_MAX_DAYS_FROM_TODAY, `days`))
    ) {
      params.toDate = format(
        moment()
          .add(ORDER_MAX_DAYS_FROM_TODAY, `days`)
          .toDate(),
        ORDER_API_DATE_FORMAT,
      );
    }

    const url = `${baseUrl}${ordersUrl}`;
    return this.httpService.get(url, { headers, params });
  };

  /**
   * Fetch all known orders addresses
   * @param {any} state The state
   * @returns {Promise<Array<OrderAddressEntity>>} The orders
   * @memberof OrderService
   */
  allAddresses = async (state: any) => {
    const headers = this.authService.createHeaders();
    const fromDate: any = format(
      moment()
        .subtract(2, `months`)
        .startOf(`month`) as any,
      ORDER_API_DATE_FORMAT,
    );
    const toDate = format(addDays(fromDate, ORDER_MAX_DAYS_FROM_TODAY), ORDER_API_DATE_FORMAT);
    const billToAccount = getFiltersSelectedAccount(state);
    const params = {
      billToAccount,
      fromDate,
      pageNumber: 1,
      pageSize: 200,
      toDate,
    };
    const url = `${baseUrl}${ordersAddressesUrl}`;
    return this.httpService.get(url, { headers, params });
  };

  /**
   * getAddresses
   * @param {string} keywords keywords for adresses to contain
   * @param {string} billToAccount account for adresses
   * @returns {Observable} Obeservable
   */
  getAddresses = (keywords: any, billToAccount: any) => {
    const headers = this.authService.createHeaders();
    const fromDate = format(
      moment()
        .subtract(2, `months`)
        .startOf(`month`) as any,
      ORDER_API_DATE_FORMAT,
    );
    const toDate = format(addDays(fromDate, ORDER_MAX_DAYS_FROM_TODAY), ORDER_API_DATE_FORMAT);
    const queryParams = {
      billToAccount,
      fromDate,
      keyword: keywords,
      pageNumber: 1,
      pageSize: 200,
      toDate,
    };
    const url = buildUrl(`${baseUrl}${ordersAddressesUrl}`, queryParams);
    return ajax({
      headers,
      url,
    }).pipe(
      map((ajaxObject) => get(ajaxObject, `response.orders.addresses`, [])),
      catchError(() => of([])),
    );
  };

  /**
   * Find an order by its `date`, `orderCode`, `orderLine`
   * @param {any} dto The required params to find an `order` by its `date`, `orderCode`, `orderLine`
   * @returns {Promise<OrderEntity>} The found `order`
   * @memberof OrderService
   */
  findById = async ({ date, orderCode, orderLine, state }: any) => {
    const headers = this.authService.createHeaders();
    const billToAccount = getFiltersSelectedAccount(state);
    const params = {
      // billToAccount,
      date: format(date, ORDER_API_DATE_FORMAT),
      orderCode,
      orderLine,
    };
    const url = `${baseUrlFlitch}${orderDetailsUrl}`;
    return this.httpService.get(url, { headers, params });
  };

  /**
   * Find an order by its `orderCode`, `orderLine` and `status`
   * @param {any} dto The required params to find an `order` by its `status`, `orderCode`, `orderLine`
   * @returns {Promise<OrderEntity>} The found `order`
   * @memberof OrderService
   */
  findByStatus = async ({ orderCode, orderLine, state, status, billToAccount }: any) => {
    const headers = this.authService.createHeaders();
    const params = {
      billToAccount: billToAccount || getFiltersSelectedAccount(state),
      orderCode: orderCode.trim(),
      orderLine,
      status,
    };
    const url = `${baseUrl}${ordersDetailsDeliveriesUrl}`;
    return this.httpService.get(url, { headers, params });
  };

  /**
   * Find linked orders
   * @param {any} dto The required params to find an `order` by its `date`, `orderCode`
   * @returns {Promise<OrderEntity>} The found `order`
   * @memberof OrderService
   */
  findLinked = async ({ date, orderCode }: any) => {
    const headers = this.authService.createHeaders();
    const params = {
      date,
      orderCode,
    };
    const url = `${baseUrl}${ordersLinkedUrl}`;
    return this.httpService.get(url, { headers, params });
  };

  getActivityLog = async (order: Order) => {
    return getActivityLogService({
      authHeaders: this.authService.createHeaders(),
      httpService: this.httpService,
      order,
    });
  };

  /**
   * Gets delivery docket with specific ID
   * @param {string} ticketCode Ticket code of the docket
   * @returns {Promise<OrderEntity>} Fetched delivery docket
   * @memberof OrderService
   */
  getDeliveryDocket = async ({ orderCode, orderLine, date, ticketCode }: any) => {
    const headers = this.authService.createHeaders();
    const params = { date, docketNumber: ticketCode, orderCode, orderLine };
    const url = `${baseUrlFlitch}${BORAL_DELIVERY_DOCKET_URL}`;
    return this.httpService.get(url, { headers, params });
  };

  /**
   * Gets completed delivery details for specific order key
   * @param {string} orderKey order key of an order
   * @returns {Promise<OrderEntity>} Fetched delivery details
   * @memberof OrderService
   */
  getCompletedDelivery = async (orderKey: any) => {
    const headers = this.authService.createHeaders();
    const params = {};
    const url = `${baseUrl}${ordersCompletedDeliveryUrl}`;
    return this.httpService.get(url.replace(`ORDER_KEY`, orderKey), { headers, params });
  };

  /**
   * Get list of dockets
   * @param {any} dto The required params to get dockets from api
   * @returns {Observable} Obeservable
   */
  getDockets = ({ account, dateTo, dateFrom, keyword, order, sort, total }: any) => {
    const headers = this.authService.createHeaders();
    const built: any = {
      dateFrom: moment(dateFrom).format(`YYYY-MM-DDTHH:mm:ss`),
      dateTo: moment(dateTo).format(`YYYY-MM-DDTHH:mm:ss`),
      order,
      sort,
      total,
    };

    if (keyword) {
      built.keyword = keyword;
    }

    const url = buildUrl(`${baseUrlFlitch}${docketsListUrl.replace(`ACCOUNT`, account)}`, built);

    return ajax({
      headers,
      url,
    }).pipe(map((e) => ({ dockets: e.response })));
  };

  /**
   * Get trucks to be displayed on a map
   * @param {any} dto The required params to get dockets from api
   * @returns {Observable} Obeservable
   */
  getTrucksForMap = ({ orderCode }: any) => {
    const headers = this.authService.createHeaders();
    const url = `${baseUrlFlitch}${BORAL_TRUCKS_MAP_URL.replace(`ORDER_CODE`, orderCode.trim())}`;
    return ajax({
      headers,
      url,
    }).pipe(map((e) => ( e.response )));
  };

  /**
   * Get dockets csv
   * @param {any} dto The required params to get dockets csv from api
   * @returns {Observable} Obeservable
   */
  getDocketsDateRangeCsv = ({ account, dateTo, dateFrom, generatedOn, meta }: any) => {
    const headers = { ...this.authService.createHeaders(), 'Content-Type': `application/json` };

    const built = {
      dateFrom: moment(dateFrom).format(`YYYY-MM-DDTHH:mm:ss`),
      dateTo: moment(dateTo).format(`YYYY-MM-DDTHH:mm:ss`),
    };

    const url = buildUrl(`${baseUrl}${downloadDocketsCsvUrl.replace(`ACCOUNT`, account)}`, built);

    return ajax({
      headers,
      method: `POST`,
      responseType: `text`,
      url,
    }).pipe(
      tap((result) => {
        let csvData = result.response;
        if (!csvData) {
          csvData = [`No data available for ${moment(dateFrom).format(`YYYY-MM-DD`)} to ${moment(dateTo).format(`YYYY-MM-DD`)}`];
        }
        const blob = new Blob([csvData], { type: `text/csv` });

        if (isIOS()) {
          createContainerIframe({ blob });
          return;
        }
        const a = window.document.createElement(`a`);
        a.download = `${meta.fileNamePrefix}_${account}_${generatedOn}.csv`;
        a.href = window.URL.createObjectURL(blob);
        a.textContent = ``;
        window.document.body.appendChild(a).click();
        a.remove();
        return;
      }),
    );
  };

  /**
   * Get dockets pdf
   * @param {any} dto The required params to get dockets pdf from api
   * @returns {Observable} Obeservable
   */
  getDocketsDateRangePdf = ({ account, dateTo, dateFrom }: any) => {
    const headers = { ...this.authService.createHeaders(), 'Content-Type': `application/json` };

    const built = {
      dateFrom: moment(dateFrom).format(`YYYY-MM-DDTHH:mm:ss`),
      dateTo: moment(dateTo).format(`YYYY-MM-DDTHH:mm:ss`),
    };

    const url = buildUrl(`${baseUrl}${downloadDocketsPdfUrl.replace(`ACCOUNT`, account)}`, built);

    return ajax({
      headers,
      method: `POST`,
      // responseType: `text`,
      url,
    }).pipe(
      map((e) => {
        if (e.response) {
          return e.response;
        }
        throw new Error(`Empty result returned`);
      }),
      catchError((err) => {
        return new EmptyError() as any;
      }),
    );
  };

  /**
   * Get projects
   * @param {string} billToAccount account id to retrieve projects for
   * @returns {Promise<Array>} The projects
   * @memberof UserService
   */
  getProjects = (billToAccount: any) => {
    const headers = this.authService.createHeaders();
    const url = buildUrl(`${baseUrl}${projectsUrl.replace(`ACCOUNT_ID`, helpers.getNumberFromBillTo(billToAccount))}`, {});
    return ajax({
      headers,
      url,
    })
      .pipe(
        map((ajaxObject) => ajaxObject.response || []),
        catchError(() => {
          // no need to do anything here
          return of([]);
        }),
      )
      .toPromise();
  };

  /**
   * Get accounts. This is used e.g. on notification settings panel to get list of account with associated projects
   * @returns {Promise<Array>} The projects
   * @memberof OrderService
   */
  getPlaceOrderAccounts = () => {
    const headers = this.authService.createHeaders();
    const url = `${baseUrl}${accountsUrl}?type=PLACE_ORDER`;
    return ajax({
      headers,
      url,
    })
      .pipe(
        map((ajaxObject) => ajaxObject.response || []),
        catchError(() => {
          // no need to do anything here
          return of([]);
        }),
      )
      .toPromise();
  };

  getDefaultProjectForAccount = (account: Account) => {
    return getDefaultProjectForAccountService({
      authHeaders: this.authService.createHeaders(),
      account,
    });
  };

  /**
   * Gets holiday and all the unavailable dates for specific region
   * @param {string} state trading region to fetch dates for
   * @returns {Promise<Array>} The dates
   * @memberof OrderService
   */
  getDatesDetails = (state: any) => {
    const headers = this.authService.createHeaders();
    const url = buildUrl(`${baseUrl}${holidaysUrl}`, { state });
    return ajax({
      headers,
      url,
    })
      .pipe(map((ajaxObject) => ajaxObject.response || []))
      .toPromise();
  };

  /**
   * Get products list
   * @param {string} projectId The project id
   * @returns {Promise<Array>} The configs
   * @memberof OrderService
   */
  getProducts = ({ projectId, locationCode, latitude, longitude }: any) => {
    const headers = this.authService.createHeaders();

    const built: any = {
      locationCode,
      latitude,
      longitude,
    };

    if (projectId) {
      built.projectId = projectId;
    }

    const url = buildUrl(`${baseUrl}${productsUrl}`, built);
    return ajax({
      headers,
      url,
    })
      .pipe(
        map((ajaxObject) => ajaxObject.response || []),
        catchError(() => {
          // no need to do anything here
          return of([]);
        }),
      )
      .toPromise();
  };

  /**
   * Get dockets pdf
   * @param {any} dto The required params to get dockets pdf from api
   * @returns {Observable} Obeservable
   */
  getDocketsPdf = ({ account, dockets }: any) => {
    const headers = { ...this.authService.createHeaders(), 'Content-Type': `application/json` };
    const url = `${baseUrl}${downloadDocketsPdfUrl.replace(`ACCOUNT`, account)}`;
    return ajax({
      url,
      method: `POST`,
      headers,
      body: { dockets },
    }).pipe(
      map((e) => e.response),
      catchError((err) => new EmptyError() as any),
    );
  };

  /**
   * creates iframe to display blob in
   * @returns {void} void
   * @memberof OrderService
   */
  createContainerIframe = ({ blob }: any) => {
    let csvIframe: any = window.document.createElement(`iframe`);
    // this is fix for IOS that removes "blob" from url when blob is open. By default when opening "blob" ios shows "blob" in url and this was flagged as an issue in CX-3515. It should display domain name at the top or other meningfull title and blob apparently might be confusing.
    // Now this is default browser behaviour and "blob" is actually part of the url itself so modifying this in url itself is out of question. The only way I found to remove it is to create iFrame and in there load the blob document into. Now then IOS seems to have an issue with back button as when browser back button is clicked safari never loads previous page. I found out that adding empty iframe, then removing and adding actual iframe where csv is loaded is the only way to get it working. User never exeprience this switch and it works as expected
    window.document.body.appendChild(csvIframe);
    csvIframe.remove();

    // now actual iframe
    csvIframe = window.document.createElement(`iframe`);
    csvIframe.id = `csvIframe`;
    csvIframe.style = `border: 0;overflow: auto;position: absolute; width: 100vw; height: 100vh; top: 0; left: 0; z-index: 999999999;background-color: #fff;`;
    window.document.body.appendChild(csvIframe);

    let clickable = window.document.createElement(`a`);
    clickable = window.document.createElement(`a`);
    clickable.href = window.URL.createObjectURL(blob);
    (document!.getElementById(`csvIframe`)! as any).contentDocument!.children[0].children[1].appendChild(clickable);
    clickable.click();
    clickable.remove();

    /**
     * Removes inserted frame and listener to avoid mem. leaks
     * @returns {void} void
     * @memberof OrderService
     */
    const removeIframe = () => {
      csvIframe.remove();
      window.removeEventListener(`popstate`, removeIframe);
      return;
    };

    window.addEventListener(`popstate`, removeIframe);
  };

  /**
   * Determines whether the order can be cancelled
   * @param {any} entry The entry object
   * @param {any} summary The summary object
   * @param {any} details The details object
   * @returns {boolean} Whether or not to show user is a Super User and if the order status is 'To Be Confirmed'
   * @memberof OrderService
   */
  canCancelOrder = (entry: any, summary: any, details: any) => {
    if (!FEATURE_TOGGLE_OKTA_SUPPORT) {
      return false;
    }
    if (entry.name !== ENTRY_PORTAL_CUSTOMER_NAME) {
      return false;
    }
    const { startTime, status } = summary;
    if (status === ORDER_STATUS_CANCELLED) {
      return false;
    }
    const {
      qty: { dispatched },
    } = details;
    if (dispatched > 0 || dispatched == null) {
      return false;
    }
    const canCancel = isBeforeCutOff(startTime, ORDER_CANCEL_CUTOFF_TIME, ORDER_CANCEL_CUTOFF_DAYS_FROM_TODAY);
    if (!canCancel) {
      return false;
    }
    return true;
  };

  /**
   * Determines whether the order can be confirmed
   * @param {any} entry The entry object
   * @param {any} summary The summary object
   * @returns {boolean} Whether or not to show user is a Super User and if the order status is 'To Be Confirmed'
   * @memberof OrderService
   */
  canConfirmOrder = (entry: any, summary: any) => {
    if (!FEATURE_TOGGLE_OKTA_SUPPORT) {
      return false;
    }
    const { startTime, status } = summary;
    if (entry.name !== ENTRY_PORTAL_CUSTOMER_NAME) {
      return false;
    }
    if (status !== ORDER_STATUS_TO_BE_CONFIRMED) {
      return false;
    }
    const canConfirm = isBeforeCutOff(startTime, ORDER_CONFIRM_CUTOFF_TIME, ORDER_CONFIRM_CUTOFF_DAYS_FROM_TODAY);
    if (!canConfirm) {
      return false;
    }
    return true;
  };

  /**
   * Cancel an Order by its `date`,`orderCode`
   * @param {any} dto The required params to cancel an `order` by its `orderCode`, `date`
   * @returns {Promise<OrderEntity>} The cancelled `order`
   * @memberof OrderService
   */
  cancelOrder = async ({ companyCode, date, orderCode, orderKey, timezone }: any) => {
    const headers = this.authService.createHeaders();
    const params = {
      companyCode,
      date: format(date, ORDER_API_DATE_FORMAT),
      orderCode,
      orderKey,
      status: `cancel`,
      timezone,
    };
    const url = `${baseUrl}${ordersConfirmationUrl}`;
    return this.httpService.put(url, { headers, params });
  };

  /**
   * Confirm an Order by its `date`,`orderCode`
   * @param {any} dto The required params to confirm an `order` by its `orderCode`, `date`
   * @returns {Promise<OrderEntity>} The confirm `order`
   * @memberof OrderService
   */
  confirmOrder = async ({ date, orderCode, orderKey }: any) => {
    const headers = this.authService.createHeaders();
    const params = {
      date: format(date, ORDER_API_DATE_FORMAT),
      orderCode,
      orderKey,
      status: `confirm`,
    };
    const url = `${baseUrl}${ordersConfirmationUrl}`;
    return this.httpService.put(url, { headers, params });
  };

  /**
   * Gets the unique status id
   * @param {any} dto The required params to get a unique status id by its `status`, `orderCode`, `orderLine`
   * @returns {Promise<OrderEntity>} The found `order`
   * @memberof OrderService
   */
  getStatusId = ({ orderCode, orderLine, status }: any) => {
    return `${status}-${orderCode}-${orderLine}`;
  };

  /**
   * placeOrder
   * @param {Object} payload payload to paas to backend
   * @returns {Promise} promise
   */
  placeOrder = async (payload: any) => {
    const headers = this.authService.createHeaders();
    const body = payload;
    return ajax({
      body,
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `POST`,
      url: `${baseUrl}${placeOrderUrl}`,
    })
      .pipe(retry(1))
      .toPromise();
  };

  updateOrder = async (payload: UpdateOrderServicePayload, orderKey: string) => {
    return updateOrderService({
      payload,
      orderKey,
      authHeaders: this.authService.createHeaders(),
    });
  };

  submitPlusLoad = async ({ quantity, orderKey }: { quantity: number; orderKey: string }) => {
    return submitPlusLoadService({
      authHeaders: this.authService.createHeaders(),
      quantity,
      orderKey,
    });
  };

  isTooLateForPlusLoad = async ({ orderKey }: { orderKey: string }) => {
    return isTooLateForPlusLoadService({
      authHeaders: this.authService.createHeaders(),
      httpService: this.httpService,
      orderKey,
    });
  };
}

export default OrderService;
